import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import LandingPage from "./pages/LandingPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ForgotUsernamePage from "./pages/ForgotUsernamePage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import SubscribersPage from "./pages/SubscribersPage";
import DashboardPage from "./pages/DashboardPage";
import VehiclesPage from "./pages/VehiclesPage";
import VehicleDetailPage from "./pages/VehicleDetailPage";
import DriversPage from "./pages/DriversPage";
import ExpensesPage from "./pages/ExpensesPage";
import SuppliersPage from "./pages/SuppliersPage";
import ReportsPage from "./pages/ReportsPage";
import TeamPage from "./pages/TeamPage";
import TripsPage from "./pages/TripsPage";
import CompliancePage from "./pages/CompliancePage";
import IncidentsPage from "./pages/IncidentsPage";
import ProfilePage from "./pages/ProfilePage";
import DashboardLayout from "./components/DashboardLayout";

const PLATFORM_ADMIN_EMAIL = 'hpbza@icloud.com';
const isPureAdmin = (user) =>
  (user?.email || '').toLowerCase() === PLATFORM_ADMIN_EMAIL &&
  !(typeof document !== 'undefined' && document.cookie.includes('impersonating=1'));

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-slate-200 border-t-gray-500 rounded-full animate-spin"></div>
          <span className="text-slate-500 text-sm">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Pure platform admin (not impersonating) only has access to /subscribers and /profile.
const PlatformAdminGate = ({ children }) => {
  const { user } = useAuth();
  if (isPureAdmin(user)) return <Navigate to="/subscribers" replace />;
  return children;
};

const HomeRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-3 border-slate-200 border-t-gray-500 rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!user) return <LandingPage />;
  return isPureAdmin(user)
    ? <Navigate to="/subscribers" replace />
    : <Navigate to="/dashboard" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-3 border-slate-200 border-t-gray-500 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (user) {
    return isPureAdmin(user)
      ? <Navigate to="/subscribers" replace />
      : <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          } />
          <Route path="/forgot-password" element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          } />
          <Route path="/forgot-username" element={
            <PublicRoute>
              <ForgotUsernamePage />
            </PublicRoute>
          } />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/" element={<HomeRoute />} />
          <Route element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route path="/dashboard" element={<PlatformAdminGate><DashboardPage /></PlatformAdminGate>} />
            <Route path="/vehicles" element={<PlatformAdminGate><VehiclesPage /></PlatformAdminGate>} />
            <Route path="/vehicles/:id" element={<PlatformAdminGate><VehicleDetailPage /></PlatformAdminGate>} />
            <Route path="/drivers" element={<PlatformAdminGate><DriversPage /></PlatformAdminGate>} />
            <Route path="/expenses" element={<PlatformAdminGate><ExpensesPage /></PlatformAdminGate>} />
            <Route path="/trips" element={<PlatformAdminGate><TripsPage /></PlatformAdminGate>} />
            <Route path="/suppliers" element={<PlatformAdminGate><SuppliersPage /></PlatformAdminGate>} />
            <Route path="/reports" element={<PlatformAdminGate><ReportsPage /></PlatformAdminGate>} />
            <Route path="/compliance" element={<PlatformAdminGate><CompliancePage /></PlatformAdminGate>} />
            <Route path="/incidents" element={<PlatformAdminGate><IncidentsPage /></PlatformAdminGate>} />
            <Route path="/team" element={<PlatformAdminGate><TeamPage /></PlatformAdminGate>} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/subscribers" element={<SubscribersPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </AuthProvider>
  );
}

export default App;
